import { ConfigUiHints, JsonSchema } from '../types'

export function schemaType(schema: JsonSchema): string | undefined {
  if (!schema) return undefined
  if (Array.isArray(schema.type)) {
    const filtered = schema.type.filter((t) => t !== 'null')
    return filtered[0] ?? schema.type[0]
  }
  return schema.type
}

export function defaultValue(schema?: JsonSchema): unknown {
  if (!schema) return ''
  if (schema.default !== undefined) return schema.default
  const type = schemaType(schema)
  switch (type) {
    case 'object':
      return {}
    case 'array':
      return []
    case 'boolean':
      return false
    case 'number':
    case 'integer':
      return 0
    case 'string':
      return ''
    default:
      return ''
  }
}

/** Join string segments of a config path with `.`, skipping numeric (array index) segments. */
export function pathKey(path: Array<string | number>): string {
  return path.filter((segment) => typeof segment === 'string').join('.')
}

/** Look up a UI hint for a config path, falling back to wildcard `*` matching. */
export function hintForPath(path: Array<string | number>, hints: ConfigUiHints) {
  const key = pathKey(path)
  const direct = hints[key]
  if (direct) return direct
  const segments = key.split('.')
  for (const [hintKey, hint] of Object.entries(hints)) {
    if (!hintKey.includes('*')) continue
    const hintSegments = hintKey.split('.')
    if (hintSegments.length !== segments.length) continue
    let match = true
    for (let i = 0; i < segments.length; i += 1) {
      if (hintSegments[i] !== '*' && hintSegments[i] !== segments[i]) {
        match = false
        break
      }
    }
    if (match) return hint
  }
  return undefined
}

/** Convert camelCase or snake_case to "Title Case" for display labels. */
export function humanize(raw: string) {
  return raw
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .replace(/^./, (m) => m.toUpperCase())
}

/** Heuristic check for paths likely containing secrets (tokens, passwords, keys). */
export function isSensitivePath(path: Array<string | number>): boolean {
  const key = pathKey(path).toLowerCase()
  return (
    key.includes('token') ||
    key.includes('password') ||
    key.includes('secret') ||
    key.includes('apikey') ||
    key.endsWith('key')
  )
}
